import React, { useState, useEffect, useRef } from 'react';
import { 
  Brain, 
  Target, 
  ShieldCheck, 
  Scale, 
  Heart, 
  Clock, 
  Users, 
  Award, 
  RefreshCcw, 
  Sparkles,
  CheckCircle2
} from 'lucide-react';

const benefits = [
  {
    icon: Brain,
    title: 'Claritate emoțională',
    description: 'Elimini nesiguranța și confuzia, permițându-ți să gândești limpede.',
    color: 'from-blue-500/20 to-cyan-500/20',
    iconColor: 'text-blue-400'
  },
  {
    icon: Target,
    title: 'Control asupra deciziilor',
    description: 'Poți alege rațional dacă vrei să rămâi, să negociezi sau să pleci.',
    color: 'from-purple-500/20 to-pink-500/20',
    iconColor: 'text-purple-400'
  },
  {
    icon: ShieldCheck,
    title: 'Evitarea manipulării',
    description: 'Dovezile împiedică versiuni contradictorii și jocuri emoționale.',
    color: 'from-green-500/20 to-emerald-500/20',
    iconColor: 'text-green-400'
  },
  {
    icon: Scale,
    title: 'Protecție legală',
    description: 'Probele pot fi folosite în procese civile (divorț, partaj, custodie) sau pentru acorduri financiare.',
    color: 'from-amber-500/20 to-orange-500/20',
    iconColor: 'text-amber-400'
  },
  {
    icon: Heart,
    title: 'Siguranța personală',
    description: 'Identifici riscuri precum fraudă, abuz sau transmitere boli sexuale.',
    color: 'from-red-500/20 to-rose-500/20',
    iconColor: 'text-red-400'
  },
  {
    icon: Clock,
    title: 'Economie de timp și resurse',
    description: 'Iei decizii rapide care evită prelungirea unei relații toxice.',
    color: 'from-teal-500/20 to-cyan-500/20',
    iconColor: 'text-teal-400'
  },
  {
    icon: Users,
    title: 'Sprijin pentru recuperare',
    description: 'Adevărul facilitează accesul la consiliere și vindecare direcționată.',
    color: 'from-indigo-500/20 to-violet-500/20',
    iconColor: 'text-indigo-400'
  },
  {
    icon: Award,
    title: 'Restaurează demnitatea',
    description: 'Ai certitudinea necesară pentru a-ți proteja interesele și imaginea.',
    color: 'from-yellow-500/20 to-amber-500/20',
    iconColor: 'text-yellow-400'
  },
  {
    icon: RefreshCcw,
    title: 'Prevenirea repetiției',
    description: 'Clarificarea comportamentului partenerului poate împiedica repetarea înșelăciunii.',
    color: 'from-sky-500/20 to-blue-500/20',
    iconColor: 'text-sky-400'
  },
  {
    icon: Sparkles,
    title: 'Pace și încheiere',
    description: 'Obții închiderea necesară pentru a merge mai departe, cu sau fără partener.',
    color: 'from-fuchsia-500/20 to-pink-500/20',
    iconColor: 'text-fuchsia-400'
  }
];

const BenefitsSection = () => {
  const [visibleItems, setVisibleItems] = useState([]);
  const sectionRef = useRef(null);
  const itemRefs = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = parseInt(entry.target.dataset.index);
            setVisibleItems((prev) => [...new Set([...prev, index])]);
          }
        });
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    itemRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <section className="py-12 bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-10">
          <span className="inline-flex items-center gap-2 text-amber-400 font-semibold text-sm uppercase tracking-wider mb-4">
            <CheckCircle2 className="w-5 h-5" />
            Detectiv Particular Bacau - Avantaje
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6">
            Avantajele Aflării Adevărului în<br />
            <span className="text-amber-400">Cazurile de Infidelitate</span>
          </h2>
          <p className="text-slate-300 text-lg md:text-xl max-w-3xl mx-auto leading-relaxed">
            Clar, concis, orientat spre decizie — iată de ce cunoașterea adevărului 
            îți oferă puterea de a-ți controla viitorul.
          </p>
        </div>

        {/* Benefits Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {benefits.map((benefit, index) => {
            const IconComponent = benefit.icon;
            const isVisible = visibleItems.includes(index);
            
            return (
              <div
                key={index}
                ref={(el) => (itemRefs.current[index] = el)}
                data-index={index}
                className={`group relative bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6 
                  hover:border-slate-600 hover:bg-slate-800/70 transition-all duration-500
                  ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${index * 80}ms` }}
              >
                {/* Gradient background on hover */}
                <div className={`absolute inset-0 bg-gradient-to-br ${benefit.color} rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                
                <div className="relative">
                  {/* Number badge */}
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-slate-700/80 rounded-full flex items-center justify-center">
                    <span className="text-amber-400 text-sm font-bold">{index + 1}</span>
                  </div>
                  
                  {/* Icon */}
                  <div className={`w-14 h-14 bg-slate-700/50 rounded-xl flex items-center justify-center mb-5 
                    group-hover:scale-110 transition-transform duration-300`}>
                    <IconComponent className={`w-7 h-7 ${benefit.iconColor}`} />
                  </div>

                  {/* Title */}
                  <h3 className="text-xl font-bold text-white mb-3 group-hover:text-white transition-colors">
                    {benefit.title}
                  </h3>

                  {/* Description */}
                  <p className="text-slate-400 text-base leading-relaxed group-hover:text-slate-300 transition-colors">
                    {benefit.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="mt-10 text-center">
          <div className="bg-gradient-to-r from-amber-500/10 via-amber-500/20 to-amber-500/10 border border-amber-500/30 rounded-2xl p-8 md:p-10 max-w-4xl mx-auto">
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Ai nevoie de un Detectiv din Bacau de încredere?
            </h3>
            <p className="text-slate-300 text-lg mb-8 max-w-2xl mx-auto">
              Contactează Detectivii Particulari Bacau pentru o consultație gratuită și confidențială. 
              Suntem aici să te ajutăm să iei cea mai bună decizie pentru viitorul tău.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="tel:+40725040500"
                className="inline-flex items-center justify-center gap-3 bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold text-lg px-8 py-4 rounded-xl transition-colors"
              >
                <span className="text-2xl">📞</span>
                Sună: +40 725 040 500
              </a>
              <a 
                href="#contact"
                className="inline-flex items-center justify-center gap-2 bg-slate-700 hover:bg-slate-600 text-white font-semibold text-lg px-8 py-4 rounded-xl transition-colors"
              >
                Completează Formularul
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BenefitsSection;
